/**
 * ReactionChips — the read-only row of twin emoji reactions under a room
 * message (Step 11, SPEC section 2).
 *
 * One chip per emoji: native emoji, count hidden at 1, sorted by count then
 * first arrival, at most six chips then a `+N` chip. Reactor first names show
 * in the design-system Tooltip on hover and keyboard focus. Only reactions
 * that arrived live pop; snapshot loads (page load, reconnect) never animate.
 */

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import { ChatRoomParticipantDto, ChatRoomReactionDto } from '@digital-twins/shared-types'

/** Chips shown before the overflow chip. */
export const MAX_VISIBLE_REACTION_CHIPS = 6

/** Names listed in a tooltip before "and N more". */
const MAX_TOOLTIP_NAMES = 3

/** How long a live chip keeps its highlight ring. */
const LIVE_HIGHLIGHT_MS = 1500

export interface ReactionChip {
  emoji: string
  count: number
  /** Reactor first names, in arrival order. */
  names: Array<string>
  /** True when any reaction in the chip arrived live. */
  isLive: boolean
}

interface ReactionChipsProps {
  messageId: string
  /** This message's resolved reactions (any order). */
  reactions: Array<ChatRoomReactionDto>
  participants: Array<ChatRoomParticipantDto>
  /** `reactionKey` values of reactions that arrived live. */
  liveReactionKeys?: ReadonlySet<string>
  /** `end` for user messages (twins react to the moderator question). */
  align?: 'start' | 'end'
}

function firstName(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] ?? ''
}

function reactionLiveKey(reaction: ChatRoomReactionDto): string {
  return `${reaction.messageSequenceIndex}|${reaction.by}|${reaction.emoji}`
}

/**
 * Groups reactions into chips: count desc, then first arrival (`at`, then
 * the order the server listed them). Pure, so ordering is unit-tested.
 */
export function buildReactionChips(
  reactions: Array<ChatRoomReactionDto>,
  participants: Array<ChatRoomParticipantDto>,
  liveReactionKeys: ReadonlySet<string> = new Set(),
): Array<ReactionChip> {
  const nameByPersona = new Map(participants.map((participant) => [participant.personaId, firstName(participant.displayName)]))
  const ordered = reactions
    .map((reaction, index) => ({ reaction, index }))
    .sort((a, b) => (a.reaction.at < b.reaction.at ? -1 : a.reaction.at > b.reaction.at ? 1 : a.index - b.index))
  const chips = new Map<string, ReactionChip & { firstArrival: number }>()
  ordered.forEach(({ reaction }, arrival) => {
    const chip = chips.get(reaction.emoji) ?? { emoji: reaction.emoji, count: 0, names: [], isLive: false, firstArrival: arrival }
    chip.count += 1
    const name = nameByPersona.get(reaction.by)
    if (name) {
      chip.names.push(name)
    }
    chip.isLive = chip.isLive || liveReactionKeys.has(reactionLiveKey(reaction))
    chips.set(reaction.emoji, chip)
  })
  return [...chips.values()]
    .sort((a, b) => b.count - a.count || a.firstArrival - b.firstArrival)
    .map(({ firstArrival: _firstArrival, ...chip }) => chip)
}

function useReducedMotion(): boolean {
  const [isReduced, setIsReduced] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return
    }
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    setIsReduced(query.matches)
  }, [])

  return isReduced
}

function LiveChipRing(props: { isLive: boolean }) {
  const { isLive } = props

  const [isLit, setIsLit] = useState(isLive)

  useEffect(() => {
    if (!isLive) {
      setIsLit(false)
      return
    }
    setIsLit(true)
    const timer = setTimeout(() => setIsLit(false), LIVE_HIGHLIGHT_MS)
    return () => clearTimeout(timer)
  }, [isLive])

  return (
    <span
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-0 rounded-full ring-2 ring-twin-chat-accent/35 transition-opacity duration-[1500ms]',
        isLit ? 'opacity-100' : 'opacity-0',
      )}
    />
  )
}

export function ReactionChips(props: ReactionChipsProps) {
  const { messageId, reactions, participants, liveReactionKeys, align = 'start' } = props

  const { t, i18n } = useTranslation('app')

  const isReducedMotion = useReducedMotion()
  const chips = useMemo(
    () => buildReactionChips(reactions, participants, liveReactionKeys),
    [reactions, participants, liveReactionKeys],
  )

  const formatNames = (names: Array<string>): string => {
    const list = new Intl.ListFormat(i18n.language, { type: 'conjunction' })
    if (names.length <= MAX_TOOLTIP_NAMES) {
      return list.format(names)
    }
    const shown = new Intl.ListFormat(i18n.language, { type: 'unit', style: 'short' })
      .format(names.slice(0, MAX_TOOLTIP_NAMES))
    return t('roomThread.namesOverflow', { names: shown, count: names.length - MAX_TOOLTIP_NAMES })
  }

  const visible = chips.slice(0, MAX_VISIBLE_REACTION_CHIPS)
  const hidden = chips.slice(MAX_VISIBLE_REACTION_CHIPS)
  const hiddenList = hidden
    .flatMap((chip) => (chip.names.length > 0 ? chip.names.map((name) => `${chip.emoji} ${name}`) : [chip.emoji]))
    .join(' · ')

  const chipClass = 'relative inline-flex h-6 cursor-default items-center gap-1 rounded-full border border-twin-chat-divider/80 bg-twin-chat-surface px-2 text-xs tabular-nums text-twin-chat-fg-muted hover:bg-twin-chat-bubble/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

  const overflowChip = hidden.length > 0 ? (
    <Tooltip>
      <TooltipTrigger
        type="button"
        className={chipClass}
        aria-label={t('roomThread.moreReactions', { count: hidden.length })}
        data-testid={`message-reactions-more-${messageId}`}
      >
        +{hidden.length}
      </TooltipTrigger>
      <TooltipContent>{hiddenList}</TooltipContent>
    </Tooltip>
  ) : null

  if (chips.length === 0) {
    return null
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div
        role="group"
        aria-label={t('roomThread.reactionsGroup')}
        className={cn('mt-1 flex flex-wrap gap-1', align === 'end' ? 'justify-end' : 'justify-start')}
        data-testid={`message-reactions-${messageId}`}
      >
        {visible.map((chip, index) => {
          const names = formatNames(chip.names)
          const shouldPop = chip.isLive && !isReducedMotion
          return (
            <Tooltip key={chip.emoji}>
              <TooltipTrigger
                type="button"
                className={cn(chipClass, shouldPop && 'animate-in zoom-in-50 duration-200')}
                aria-label={t('roomThread.reactionAria', { emoji: chip.emoji, count: chip.count, names })}
                data-testid={`message-reaction-${messageId}-${index}`}
                data-live={chip.isLive ? 'true' : undefined}
              >
                {shouldPop && <LiveChipRing isLive={shouldPop} />}
                <span className="text-sm leading-none">{chip.emoji}</span>
                {chip.count > 1 && <span>{chip.count}</span>}
              </TooltipTrigger>
              {names && <TooltipContent>{names}</TooltipContent>}
            </Tooltip>
          )
        })}
        {overflowChip}
      </div>
    </TooltipProvider>
  )
}
